import React, { useState, useMemo, useRef, useEffect } from "react";
import { AgGridReact } from 'ag-grid-react';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import Button from 'react-bootstrap/Button';
import Form from 'react-bootstrap/Form';
import Container from 'react-bootstrap/Container';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import InputGroup from 'react-bootstrap/InputGroup';
import 'ag-grid-enterprise';
import ConfirmDeleteModal from "../confirmdeletemodal";
import loaderGif from "../../../assets/loader.gif";
import api from '../../../config/axiosConfig';


const GeneralPreferences = () => {
    const [formData, setFormData] = useState({
        id: null,  // Track if it's a new entry or an edit
        prefKey: '',
        value: '',
        isActive: true,
        createddate: '',
        modifieddate: ''
    });
    const [rowData, setRowData] = useState([]);
    const [isEditMode, setIsEditMode] = useState(false); // To track if we are in edit mode
    const [showModal, setShowModal] = useState(false);
    const [selectedRow, setSelectedRow] = useState(null);
    const [loader, setLoader] = useState(false);
    const [showDropdown, setShowDropdown] = useState(false);
    const [suggestions, setSuggestions] = useState([]);
    const [searchItem, setSearchItem] = useState([]);

    const getData = async () => {
        try {
            const response = await api.get('/generalPreferences');
            setRowData(response.data);
            setSuggestions(response.data.map(item => item.prefKey))
        } catch (error) {
            console.error("Error fetching data:", error);
        }
    };

    useEffect(() => {
        getData();
    }, []);

    const gridRef = useRef();

    // Column definitions for Ag-Grid
    const columnDefs = [

        { headerName: 'ID', field: 'id', sort: "desc", filter: 'agNumberColumnFilter' },
        { headerName: 'Key', field: 'prefKey', filter: 'agTextColumnFilter' },
        { headerName: 'Value', field: 'value', filter: 'agTextColumnFilter' },
        { headerName: 'Is Active', field: 'isActive' },
        { headerName: 'Created By', field: 'createdby', filter: 'agTextColumnFilter' },
        { headerName: 'Created At', field: 'createddate', filter: 'agDateColumnFilter' },
        { headerName: 'Updated By', field: 'modifiedby', filter: 'agTextColumnFilter' },
        { headerName: 'Updated At', field: 'modifieddate', filter: 'agDateColumnFilter' },
        {
            field: "actions",
            headerName: "Actions",
            cellRenderer: (params) => (
                <Button
                    variant="danger"
                    className="btn-sm"
                    onClick={() => handleDelete(params.data)}
                >
                    <i className="bi bi-trash3"></i>
                </Button>
            ),
        },
    ];

    const sideBar = useMemo(() => {
        return {
            toolPanels: [
                {
                    id: 'columns',
                    labelDefault: 'Columns',
                    labelKey: 'columns',
                    iconKey: 'columns',
                    toolPanel: 'agColumnsToolPanel',
                },
                {
                    id: 'filters',
                    labelDefault: 'Filters',
                    labelKey: 'filters',
                    iconKey: 'filter',
                    toolPanel: 'agFiltersToolPanel',
                }
            ],
            defaultToolPanel: '',
        };
    }, []);

    // Server-Side Datasource
    const datasource = {
        getRows: async function (params) {
            try {
                const { request } = params;

                const response = await api.post('/generalPreferences/ag', { ...request });

                const { rows, lastRow } = response.data;

                // Send data back to AG Grid
                params.successCallback(rows, lastRow);
            } catch (error) {
                console.error("Error fetching rows:", error);
                params.failCallback();
            }
        }
    };

    const gridOptions = {
        rowModelType: 'serverSide',
        columnDefs: columnDefs,
        rowHeight: 60,

        defaultColDef: {
            sortable: true,
            filter: true,
            resizable: true,
            floatingFilter: true,
            enableValue: true,
            enableRowGroup: true,
            enablePivot: true,
            filterParams: {
                buttons: ["reset"],
                closeOnApply: true,
            },
        },

        sideBar: sideBar,
        rowGroupPanelShow: 'always',
        rowSelection: 'single',
        columnHoverHighlight: true,
        suppressRowGroupHidesColumns: true,
        cacheBlockSize: 20,
    }

    // Attach the datasource when the grid is ready
    const onGridReady = (params) => {
        params.api.setServerSideDatasource(datasource);
    };

    // Row selection handler
    const onRowSelected = (event) => {
        if (event.node.isSelected()) {
            const selectedRow = event.data;
            setFormData({
                id: selectedRow.id,
                prefKey: selectedRow.prefKey,
                value: selectedRow.value,
                isActive: selectedRow.isActive
            });

            setIsEditMode(true);  // Switch to edit mode
            setSelectedRow(selectedRow)
        }
    };

    // Handle form input changes
    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prevState => ({
            ...prevState,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    // Handle form submission
    // Handle form submission for adding/updating media
    const handleAddData = async (e) => {
        e.preventDefault();
        setLoader(true);
        // Prepare data for media entry
        const generalPreferencesData = {
            prefKey: formData.prefKey,
            value: formData.value,
            isActive: formData.isActive,
            createdat: new Date(),

        };
        const apiResponse = await api.get('/generalPreferences');
        const existingPreference = apiResponse.data.find(item => item.prefKey === formData.prefKey);
        // Determine whether to update or create a new entry
        const url = existingPreference ? `/generalPreferences/${existingPreference.id}` : '/generalPreferences';
        const request = existingPreference ? api.put(url, generalPreferencesData) : api.post(url, generalPreferencesData);

        try {
            const response = await request;

            // Clear form fields and reset edit mode
            setFormData({ id: null, prefKey: '', value: '', isActive: true });
            setIsEditMode(false);
            setLoader(false);
            gridRef.current.api.refreshServerSideStore({ purge: true });

        } catch (error) {
            console.error("Error adding/updating general preferences:", error);
            setLoader(false);
        }
    };


    // Reset form for new entry
    const handleNewEntry = () => {
        setFormData({ id: null, prefKey: '', value: '', isActive: true });
        setIsEditMode(false);  // Switch to add mode
    };



    const handleDelete = () => {
        setShowModal(true);
    };

    const confirmDelete = async () => {
        try {
            await api.delete(`/generalPreferences/${selectedRow.id}`);
            setShowModal(false);
            gridRef.current.api.refreshServerSideStore({ purge: true });
        } catch (error) {
            console.error('Error deleting general preferences:', error);
        }
    };

    const handleSuggestionClick = (suggestedValue) => {
        setFormData(prevState => ({
            ...prevState,
            prefKey: suggestedValue,
        }));
    };

    const handleKeyChange = (e) => {
        const suggestedValue = e.target.value;
        setFormData(prevState => ({
            ...prevState,
            prefKey: suggestedValue,
        }));


        const filteredSuggestions = suggestions.filter(item =>
            item.toLowerCase().includes(suggestedValue.toLowerCase())
        );
        setSearchItem(filteredSuggestions);
    };


    return (
        <>
            <Container fluid>
                <Row className="mt-4">
                    <h6 className="text-start">General Preferences List</h6>
                    <div className="ag-theme-alpine" style={{ height: 400, width: '100%' }}>
                        <AgGridReact
                            ref={gridRef}
                            gridOptions={gridOptions}
                            onGridReady={onGridReady}
                            onRowSelected={onRowSelected}
                        />
                    </div>
                </Row>
                <Row className="mt-4 border shadow p-4">
                    {loader && (
                        <div className="loader-overlay">
                            <img src={loaderGif} alt="Loading..." className="loader" />
                        </div>
                    )}
                    <Col md={12}>
                        <h6 className="text-start">{isEditMode ? "Edit General Preferences" : "Add General Preferences"}</h6>
                        <Form onSubmit={handleAddData}>
                            <Row>
                                <Col md={6}>
                                    <Form.Group>
                                        <Form.Label className="me-3">Key:</Form.Label>
                                        <Form.Control
                                            type="text"
                                            name="prefKey"
                                            placeholder="Enter General Preference`s name"
                                            value={formData.prefKey}
                                            onChange={handleKeyChange}
                                            onFocus={() => setShowDropdown(true)}
                                            onBlur={() => setTimeout(() => setShowDropdown(false), 200)}
                                            autoComplete="off" //will display text from browser history
                                            required
                                        />
                                        {showDropdown && suggestions.length > 0 && (
                                            <ul className="dropdown-menu show" style={{ maxHeight: '200px', overflowY: 'auto', position: 'absolute', width: '100%' }}>
                                                {searchItem.map((item, index) => (
                                                    <li
                                                        key={index}
                                                        className="dropdown-item"
                                                        onMouseDown={() => handleSuggestionClick(item)}
                                                    >
                                                        {item}
                                                    </li>
                                                ))}
                                            </ul>
                                        )}
                                    </Form.Group>
                                </Col>
                                <Col md={6}>
                                    <Form.Group>
                                        <Form.Check
                                            type="checkbox"
                                            name="isActive"
                                            label="Is Active"
                                            checked={formData.isActive}
                                            onChange={handleChange}
                                            className="ms-4"
                                        />
                                    </Form.Group>
                                </Col>
                            </Row><br />
                            <Row className="mb-4">
                                <Col md={6}>
                                    <Form.Group>
                                        <Form.Label>Value</Form.Label>
                                        <Form.Control
                                            type="text"
                                            name="value"
                                            placeholder="Enter General Preference`s value"
                                            value={formData.value}
                                            onChange={handleChange}
                                            required
                                        />
                                    </Form.Group>
                                </Col>
                            </Row>
                            <Button variant="primary" type="submit">
                                {isEditMode ? "Update" : "Add"}
                            </Button>
                            {isEditMode ?
                                <Button variant="secondary" className="ms-3" onClick={handleNewEntry}>
                                    Create New Entry
                                </Button>
                                : ''
                            }
                        </Form>
                    </Col>
                </Row>
            </Container>

            {/* Modal for confirming deletion */}
            <ConfirmDeleteModal
                show={showModal}
                handleClose={() => setShowModal(false)}
                handleConfirm={confirmDelete}
                title="Confirm Deletion"
                body={`Are you sure you want to delete ${selectedRow?.prefKey}?`}
                confirmText="Delete"
            />
        </>
    );
};

export default GeneralPreferences;